import axios from "axios";
import { api } from "../config";

// Set baseURL - ensure it's set correctly
const API_URL = api.API_URL || process.env.REACT_APP_API_URL || "http://localhost:5001/api";
axios.defaults.baseURL = API_URL;

// Debug: Log the baseURL being used
console.log("🔧 API Base URL configured:", axios.defaults.baseURL);
// Do NOT set axios.defaults.headers.post Content-Type to application/json — it breaks
// multipart/form-data (file uploads): the boundary is never set and auth/body can fail.

function getAuthTokenFromStorage() {
  try {
    const raw = localStorage.getItem("authUser");
    if (!raw) return null;
    const authUser = JSON.parse(raw);
    let t = authUser.token || authUser.accessToken || null;
    if (typeof t === "string" && t.startsWith("Bearer ")) {
      t = t.slice(7).trim();
    }
    return t || null;
  } catch {
    return null;
  }
}

const initialToken = getAuthTokenFromStorage();
if (initialToken) {
  axios.defaults.headers.common["Authorization"] = "Bearer " + initialToken;
}

// Every request: fresh token + correct headers for file uploads (FormData must not use application/json)
axios.interceptors.request.use(
  (config) => {
    const token = getAuthTokenFromStorage();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      delete config.headers.Authorization;
    }
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      const h = config.headers;
      if (h) {
        if (typeof h.delete === "function") {
          h.delete("Content-Type");
          h.delete("content-type");
        }
        delete h["Content-Type"];
        delete h["content-type"];
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// intercepting to capture errors
axios.interceptors.response.use(
  function (response) {
    return response.data ? response.data : response;
  },
  function (error) {
    const status = error.response?.status || error.status;
    const cfg = error.config || {};
    const fullUrl = String(cfg.baseURL || "") + String(cfg.url || "");
    const isFormData = typeof FormData !== "undefined" && cfg.data instanceof FormData;

    // Always log upload / auth failures (browser DevTools Console → filter "API")
    if (isFormData || status === 401 || status === 403 || fullUrl.includes("home-ad-banners")) {
      const hdr = cfg.headers || {};
      const authVal = hdr.Authorization || hdr.common?.Authorization || hdr.get?.("Authorization");
      console.error("[API]", isFormData ? "FormData upload error" : "Request error", {
        httpStatus: status,
        method: cfg.method,
        url: fullUrl,
        hasAuthorizationHeader: !!authVal,
        tokenPresentInStorage: !!getAuthTokenFromStorage(),
        responseBody: error.response?.data,
        axiosCode: error.code,
        axiosMessage: error.message,
      });
    }

    let message;
    if (error.code === "ECONNREFUSED" || error.message?.includes("Network Error")) {
      message = {
        error: "Cannot connect to backend server. Make sure the server is running on http://localhost:5001",
        message: error.message || "Network Error",
        _httpStatus: status,
        _requestUrl: fullUrl,
      };
    } else {
      const data = error.response?.data;
      const base =
        typeof data === "object" && data !== null
          ? { ...data }
          : { error: error.message || "Request failed" };

      switch (status) {
        case 500:
          message = { ...base, error: base.error || "Internal Server Error", _httpStatus: status, _requestUrl: fullUrl };
          break;
        case 401:
          message = { ...base, error: base.error || "Invalid credentials", _httpStatus: status, _requestUrl: fullUrl };
          break;
        case 403:
          message = {
            ...base,
            error: base.error || "Access denied",
            code: base.code,
            detail: base.detail,
            _httpStatus: status,
            _requestUrl: fullUrl,
          };
          break;
        case 404:
          message = {
            ...base,
            error: base.error || "Endpoint not found",
            _httpStatus: status,
            _requestUrl: fullUrl,
          };
          break;
        default:
          message = {
            ...base,
            error: base.error || error.message || "Request failed",
            _httpStatus: status,
            _requestUrl: fullUrl,
          };
      }
    }
    return Promise.reject(message);
  }
);

/**
 * Sets the default authorization
 * @param {*} token
 */
export const setAuthorization = (token) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = "Bearer " + token;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

/**
 * Clears the authorization header
 */
export const clearAuthorization = () => {
  delete axios.defaults.headers.common["Authorization"];
};

class APIClient {
  /**
   * Fetches data from given url
   */
  get = (url, params) => {
    // Ensure baseURL is set
    if (!axios.defaults.baseURL) {
      const API_URL = api.API_URL || process.env.REACT_APP_API_URL || "http://localhost:5001/api";
      axios.defaults.baseURL = API_URL;
      console.warn("⚠️ baseURL was not set, setting it now:", API_URL);
    }
    
    // Build query string if params exist
    let queryString = "";
    if (params && Object.keys(params).length > 0) {
      const paramKeys = Object.keys(params).map(key => `${key}=${params[key]}`);
      queryString = "?" + paramKeys.join('&');
    }
    
    const fullUrl = url + queryString;
    const finalUrl = axios.defaults.baseURL + fullUrl;
    
    // Debug log
    console.log("🔍 API GET Request:", { 
      baseURL: axios.defaults.baseURL, 
      url: fullUrl, 
      finalUrl: finalUrl
    });
    
    // Use axios.get with the full URL to ensure baseURL is used
    return axios.get(fullUrl);
  };
  
  /**
   * post given data to url (JSON body)
   */
  create = (url, data) => {
    return axios.post(url, data, {
      headers: { "Content-Type": "application/json" },
    });
  };
  
  /**
   * post FormData to url (for file uploads)
   */
  createWithFormData = (url, formData) => {
    const token = getAuthTokenFromStorage();
    return axios.post(url, formData, {
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  };
  
  /**
   * Updates data (JSON body)
   */
  update = (url, data) => {
    return axios.patch(url, data, {
      headers: { "Content-Type": "application/json" },
    });
  };
  
  /**
   * Update with FormData (for file uploads)
   */
  updateWithFormData = (url, formData) => {
    const token = getAuthTokenFromStorage();
    return axios.patch(url, formData, {
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  };

  put = (url, data) => {
    return axios.put(url, data, {
      headers: { "Content-Type": "application/json" },
    });
  };
  
  /**
   * Delete
   */
  delete = (url, config) => {
    return axios.delete(url, { ...config });
  };
}

export default new APIClient();

